import { computed, inject, Injectable, Signal } from '@angular/core';
import { DocumentPathService } from '@common';
import { BenefitsListItem } from '@components';
import { CONFIG } from '@config';
import { TranslateService } from '@i18n';
import { CoverDetails } from '../../models/cover-details.interface';

@Injectable({
  providedIn: 'root',
})
export class CoverDetailsService {
  private readonly config = inject(CONFIG);
  private readonly documentPathService = inject(DocumentPathService);
  private readonly translateService = inject(TranslateService);

  readonly getInclusions: Signal<string[]> = computed(() => [
    this.translateService.translate('COVER_DETAILS_INCLUSION_BREAKDOWNS'),
    this.translateService.translate(
      'COVER_DETAILS_INCLUSION_ACCIDENTAL_DAMAGE',
    ),
    this.translateService.translate(
      'COVER_DETAILS_INCLUSION_UNLIMITED_REPAIRS',
    ),
    this.translateService.translate('COVER_DETAILS_INCLUSION_REPLACEMENT'),
  ]);

  readonly getExclusions: Signal<string[]> = computed(() => [
    this.translateService.translate(
      'COVER_DETAILS_EXCLUSION_DELIBERATE_DAMAGE',
    ),
    this.translateService.translate(
      'COVER_DETAILS_EXCLUSION_INSTALLATION_COSTS',
    ),
  ]);

  readonly getCoveredItems: Signal<BenefitsListItem[]> = computed(() => [
    {
      iconName: 'shield-check',
      text: this.translateService.translate(
        'COVER_DETAILS_COVERED_IMMEDIATE_ACCIDENTAL_DAMAGE',
      ),
    },
    {
      iconName: 'screwdriver-wrench',
      text: this.translateService.translate(
        'COVER_DETAILS_COVERED_UNLIMITED_CALLOUTS',
      ),
    },
    {
      iconName: 'dolly',
      text: this.translateService.translate(
        'COVER_DETAILS_COVERED_REPLACEMENT',
      ),
    },
    {
      iconName: 'house-circle-check',
      text: this.translateService.translate(
        'COVER_DETAILS_COVERED_BREAKDOWN_AFTER_GUARANTEE',
      ),
    },
  ]);

  readonly getNotCoveredItems: Signal<BenefitsListItem[]> = computed(() => [
    {
      iconName: 'xmark',
      text: this.translateService.translate(
        'COVER_DETAILS_EXCLUSION_DELIBERATE_DAMAGE',
      ),
    },
    {
      iconName: 'xmark',
      text: this.translateService.translate(
        'COVER_DETAILS_EXCLUSION_INSTALLATION_COSTS',
      ),
    },
  ]);

  readonly getAdditionalItems: Signal<BenefitsListItem[]> = computed(() => [
    {
      iconName: 'screwdriver-wrench',
      text: this.translateService.translate(
        'COVER_DETAILS_ADDITIONAL_EXPERT_REPAIRS',
      ),
    },
    {
      iconName: 'arrow-pointer',
      text: this.translateService.translate(
        'COVER_DETAILS_ADDITIONAL_ONLINE_BOOKING',
      ),
    },
  ]);

  readonly getCoverDetails = computed<CoverDetails>(() => {
    const baseUrl = this.config.canonicalBaseUrl;

    return {
      ipid: `${baseUrl}/${this.documentPathService.ipidUrl()}`,
      termsAndConditions: `${baseUrl}/${this.documentPathService.termsAndConditionsUrl()}`,
      privacyPolicy: this.config.privacyPolicyLink ?? '',
      inclusions: this.getInclusions(),
      exclusions: this.getExclusions(),
    };
  });
}
